import { useEffect, useState } from "react";
import { getAllHandsChronological, type StoredHand } from "../history/db.js";
import { computeWinLossSummary } from "../history/summary.js";
import { chipsToBb } from "../engine/selectors.js";
import { useGameStore } from "../store/gameStore.js";
import { PnlGraph } from "./PnlGraph.js";
import { CardView } from "./CardView.js";
import { HandDetailModalLoader } from "./HandDetailModalLoader.js";

const NAMES: readonly [string, string] = ["あなた", "Bot"];
/** How many hands the "直近のハンド" list shows -- deliberately smaller than
 * the full history screen's list since this panel is a compact sidebar. */
const LIST_LIMIT = 20;
/** How many hands the PnL graph's cumulative window covers (matches HistoryScreen). */
const PNL_WINDOW = 100;

/**
 * Desktop-only sidebar (min-width: 1100px, see .side-panel in styles.css --
 * item 5 of the 2026-07-17 フィードバック): the collections-graph + win/loss
 * summary + recent-hands list that used to require navigating to the
 * history screen, now always visible alongside the game column. Reuses the
 * same data/components as HistoryScreen.
 */
export function SidePanel() {
  const [allChronological, setAllChronological] = useState<StoredHand[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // recentHands is replaced with a new array reference by the store every
  // time a hand finishes and is saved (see gameStore's finishHand) -- using
  // it as an effect dependency re-fetches from IndexedDB right after each
  // hand completes, which is what keeps this panel live without polling.
  const recentHands = useGameStore((s) => s.recentHands);

  useEffect(() => {
    let cancelled = false;
    void getAllHandsChronological().then((loaded) => {
      if (!cancelled) setAllChronological(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [recentHands]);

  // Newest-first; the summary badge looks back over more hands (PNL_WINDOW)
  // than the compact list actually displays (LIST_LIMIT), mirroring
  // HistoryScreen's summary/list split.
  const summaryHands = allChronological ? allChronological.slice(-PNL_WINDOW).reverse() : null;
  const hands = summaryHands ? summaryHands.slice(0, LIST_LIMIT) : null;
  const summary = summaryHands ? computeWinLossSummary(summaryHands) : null;

  return (
    <aside className="side-panel">
      {/* 直近のハンド moved above 収支 (2026-07-17 フィードバック #4). */}
      <h2 className="side-panel-title">直近のハンド</h2>
      <div className="side-panel-list">
        {hands?.map((h) => {
          const board = h.record.board;
          return (
            <button key={h.id} type="button" className="history-item" onClick={() => setSelectedId(h.id)}>
              <div className="history-item-top">
                <span className="history-item-time">{new Date(h.timestamp).toLocaleString("ja-JP")}</span>
                <span className={`hist-net ${h.heroNetChips >= 0 ? "result-win" : "result-loss"}`}>
                  {h.heroNetChips >= 0 ? "+" : ""}
                  {chipsToBb(h.heroNetChips).toFixed(1)}bb
                </span>
              </div>
              <div className="history-item-cards">
                <CardView card={h.record.holeCards[0][0]} size="sm" />
                <CardView card={h.record.holeCards[0][1]} size="sm" />
                {board.jokerRevealed && <CardView joker size="sm" />}
                {board.flopReal && (
                  <>
                    <CardView card={board.flopReal[0]} size="sm" />
                    <CardView card={board.flopReal[1]} size="sm" />
                  </>
                )}
                {board.turn !== undefined && <CardView card={board.turn} size="sm" />}
                {board.river !== undefined && <CardView card={board.river} size="sm" />}
              </div>
            </button>
          );
        })}
        {hands !== null && hands.length === 0 && <p className="settings-note">まだハンドがありません。</p>}
      </div>

      <h3 className="side-panel-subtitle">収支</h3>
      {allChronological && allChronological.length > 0 ? (
        <PnlGraph hands={allChronological} windowSize={PNL_WINDOW} />
      ) : (
        <p className="settings-note">まだハンドがありません。</p>
      )}

      {summary && (summary.wins > 0 || summary.losses > 0) && (
        <p className="side-panel-summary">
          {summary.wins}勝{summary.losses}敗
          {summary.streak >= 1 && `・${summary.streak}連${summary.streakType === "win" ? "勝" : "敗"}中`}
        </p>
      )}

      {selectedId !== null && (
        <HandDetailModalLoader id={selectedId} names={NAMES} onClose={() => setSelectedId(null)} />
      )}
    </aside>
  );
}
